import React, { useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import SectionHeader from '../ui/SectionHeader';
import Card from '../ui/Card';
import Chip from '../ui/Chip';
import { getActiveTheme } from '../ui/tokens';
import SegmentedControl from '../components/SegmentedControl.jsx';
import {
  fetchAdminUsers,
  fetchPlanningLookups,
  fetchVacationEntries,
  createVacationEntry,
  updateVacationEntry,
  deleteVacationEntry,
} from '../api';

const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const PERIOD_TABS = ['Q1', 'Q2', 'Q3', 'Q4', 'Year'];

function pad2(n) {
  return String(n).padStart(2, '0');
}

function formatYmdLocal(d) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function parseYmdLocal(raw) {
  const s = String(raw || '').slice(0, 10);
  const m = /^([0-9]{4})-([0-9]{2})-([0-9]{2})$/.exec(s);
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

function computePeriodRange(periodKey, year) {
  const y = Number(year);
  if (!Number.isFinite(y)) return computePeriodRange(periodKey, new Date().getFullYear());
  const from = (m, d) => formatYmdLocal(new Date(y, m, d));
  const to = (m, d) => formatYmdLocal(new Date(y, m, d));

  switch (String(periodKey || '').toUpperCase()) {
    case 'Q1':
      return { from: from(0, 1), to: to(2, 31) };
    case 'Q2':
      return { from: from(3, 1), to: to(5, 30) };
    case 'Q3':
      return { from: from(6, 1), to: to(8, 30) };
    case 'Q4':
      return { from: from(9, 1), to: to(11, 31) };
    case 'YEAR':
      return { from: from(0, 1), to: to(11, 31) };
    default:
      return { from: from(0, 1), to: to(11, 31) };
  }
}

function getCurrentPeriodKey(now = new Date()) {
  const month = now.getMonth();
  const q = Math.floor(month / 3) + 1;
  return `Q${Math.min(4, Math.max(1, q))}`;
}

function getMonthsForPeriod(periodKey) {
  const key = String(periodKey || '').toUpperCase();
  if (key === 'Q1') return [0, 1, 2];
  if (key === 'Q2') return [3, 4, 5];
  if (key === 'Q3') return [6, 7, 8];
  if (key === 'Q4') return [9, 10, 11];
  return [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
}

function enumerateYmdBetween(startYmd, endYmd) {
  const start = parseYmdLocal(startYmd);
  const end = parseYmdLocal(endYmd);
  if (!start || !end) return [];
  if (end < start) return [];

  const out = [];
  const d = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const stop = new Date(end.getFullYear(), end.getMonth(), end.getDate());
  while (d <= stop) {
    out.push(formatYmdLocal(d));
    d.setDate(d.getDate() + 1);
  }
  return out;
}

function buildDayIndex(rows) {
  const map = Object.create(null);
  (Array.isArray(rows) ? rows : []).forEach((e) => {
    const start = String(e?.start_date || '').slice(0, 10);
    const end = String(e?.end_date || '').slice(0, 10);
    const userId = String(e?.user_id || '').trim();
    const type = String(e?.type || '').trim().toUpperCase();
    if (!start || !end || !userId) return;
    if (end < start) return;
    if (type !== 'VACATION' && type !== 'SICK') return;

    enumerateYmdBetween(start, end).forEach((ymd) => {
      if (!map[ymd]) map[ymd] = { vacation: new Set(), sick: new Set() };
      if (type === 'VACATION') map[ymd].vacation.add(userId);
      if (type === 'SICK') map[ymd].sick.add(userId);
    });
  });
  return map;
}

function getUnionCount(a, b) {
  const s = new Set();
  (a instanceof Set ? a : new Set()).forEach(x => s.add(x));
  (b instanceof Set ? b : new Set()).forEach(x => s.add(x));
  return s.size;
}

function MiniMonthGrid({
  year,
  monthIndex,
  dayIndex,
  mode,
  selectedUserId,
  selectedDate,
  onSelectDate,
}) {
  const theme = getActiveTheme();
  const themePrimary = theme?.primary;
  const themePrimarySoft = theme?.primarySoft;
  const themeMuted = theme?.mutedText;

  const first = new Date(year, monthIndex, 1);
  const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
  // JS day: 0=Sun,1=Mon,... convert to Monday-first index.
  const leadingBlanks = (first.getDay() + 6) % 7;
  const totalCells = leadingBlanks + daysInMonth;
  const rows = Math.ceil(totalCells / 7);
  const cells = Array.from({ length: rows * 7 }, (_, i) => {
    const dayNum = i - leadingBlanks + 1;
    return dayNum >= 1 && dayNum <= daysInMonth ? dayNum : null;
  });

  const userKey = String(selectedUserId || '');

  return (
    <div className="rounded border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-2">
      <div className="flex items-center justify-between mb-1">
        <div className="text-xs font-semibold text-gray-800 dark:text-gray-100">{MONTH_LABELS[monthIndex]} {year}</div>
      </div>
      <div className="grid grid-cols-7 gap-1 text-[10px] text-gray-600 dark:text-gray-400">
        {WEEKDAY_LABELS.map((w) => (
          <div key={w} className="text-center">{w}</div>
        ))}
      </div>
      <div className="mt-1 grid grid-cols-7 gap-1">
        {cells.map((dayNum, idx) => {
          if (!dayNum) {
            return <div key={`b-${idx}`} className="h-7" />;
          }

          const ymd = `${year}-${pad2(monthIndex + 1)}-${pad2(dayNum)}`;
          const info = dayIndex?.[ymd];
          const vac = info?.vacation;
          const sick = info?.sick;

          const hasVacation = mode === 'all'
            ? (vac instanceof Set && vac.size > 0)
            : (vac instanceof Set && vac.has(userKey));

          const hasSick = mode === 'all'
            ? (sick instanceof Set && sick.size > 0)
            : (sick instanceof Set && sick.has(userKey));

          const countAll = mode === 'all'
            ? getUnionCount(vac, sick)
            : 0;

          const isHighlighted = hasVacation || hasSick;
          const isSelected = String(selectedDate || '') === ymd;
          const cellStyle = {
            ...(isHighlighted && themePrimarySoft ? { background: themePrimarySoft } : null),
            ...(isSelected && themePrimary ? { borderColor: themePrimary } : null),
          };

          return (
            <button
              key={ymd}
              type="button"
              onClick={() => {
                if (typeof onSelectDate !== 'function') return;
                onSelectDate((prev) => (String(prev || '') === ymd ? null : ymd));
              }}
              className={
                `h-7 rounded border text-left w-full `
                + (isSelected ? 'border-gray-900 dark:border-gray-100' : 'border-gray-200 dark:border-gray-800')
                + ' '
                + (isHighlighted ? 'bg-gray-100 dark:bg-gray-800' : 'bg-white dark:bg-gray-900')
              }
              style={cellStyle}
            >
              <div className="h-full px-1 py-0.5 flex items-start justify-between">
                <div className="text-[11px] text-gray-900 dark:text-gray-100 leading-none">{dayNum}</div>
                {mode === 'all' && countAll > 0 ? (
                  <div
                    className="text-[10px] leading-none px-1 rounded text-white"
                    style={themePrimary ? { background: themePrimary } : undefined}
                  >
                    {countAll}
                  </div>
                ) : null}
              </div>
              <div className="px-1 pb-0.5 flex items-center gap-1">
                {hasVacation ? (
                  <div
                    className="w-1.5 h-1.5 rounded-full"
                    style={themePrimary ? { background: themePrimary } : undefined}
                  />
                ) : null}
                {hasSick ? (
                  <div
                    className="w-1.5 h-1.5 rounded-full"
                    style={themeMuted ? { background: themeMuted } : undefined}
                  />
                ) : null}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function normalizeRole(v) {
  return String(v || '').trim().toLowerCase();
}

function getUserLabel(u) {
  const name = String(u?.name || u?.display_name || u?.username || '').trim();
  const email = String(u?.email || '').trim();
  if (name) return name;
  if (email) return email;
  return '—';
}

function sortUsers(rows) {
  const managerOrder = ['Vinko', 'Sanela', 'Sanele', 'Andrea'];
  const managerOrderIndex = (u) => {
    const name = String(u?.name || u?.display_name || '').trim().toLowerCase();
    const idx = managerOrder.findIndex(x => x.toLowerCase() === name);
    return idx === -1 ? 999 : idx;
  };

  const roleRank = (r) => {
    const rr = normalizeRole(r);
    if (rr === 'manager' || rr === 'admin') return 0;
    if (rr === 'junior_manager') return 1;
    return 2;
  };

  return (Array.isArray(rows) ? rows : []).slice().sort((a, b) => {
    const ra = roleRank(a.role);
    const rb = roleRank(b.role);
    if (ra !== rb) return ra - rb;

    // Specific ordering for managers.
    const aIsManager = normalizeRole(a.role) === 'manager' || normalizeRole(a.role) === 'admin';
    const bIsManager = normalizeRole(b.role) === 'manager' || normalizeRole(b.role) === 'admin';
    if (aIsManager && bIsManager) {
      const ai = managerOrderIndex(a);
      const bi = managerOrderIndex(b);
      if (ai !== bi) return ai - bi;
    }

    return getUserLabel(a).localeCompare(getUserLabel(b));
  });
}

function isManagerTestUser(u) {
  const email = String(u?.email || '').trim().toLowerCase();
  const name = String(u?.name || u?.display_name || '').trim().toLowerCase();
  const username = String(u?.username || '').trim().toLowerCase();
  return email === 'manager@local.test' || name.includes('manager test') || username === 'manager@local.test';
}

export default function Vacation({ user }) {
  const [users, setUsers] = useState([]); // [{id,name,email,role,status}]
  const [selectedUserId, setSelectedUserId] = useState('');
  const [entries, setEntries] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingEntries, setLoadingEntries] = useState(false);
  const [peopleNote, setPeopleNote] = useState('');

  const [selectedDate, setSelectedDate] = useState(null); // YYYY-MM-DD | null
  const entryListRef = useRef(null);

  const [year] = useState(() => new Date().getFullYear());
  const [periodKey, setPeriodKey] = useState(() => getCurrentPeriodKey(new Date()));
  const [range, setRange] = useState(() => computePeriodRange(getCurrentPeriodKey(new Date()), new Date().getFullYear()));
  const [calendarMode, setCalendarMode] = useState('all'); // person | all

  const todayYmd = useMemo(() => formatYmdLocal(new Date()), []);

  const selectedUser = useMemo(
    () => users.find(u => String(u.id) === String(selectedUserId)) || null,
    [users, selectedUserId]
  );

  const entriesForSelectedUser = useMemo(() => {
    const uid = String(selectedUserId || '');
    return (Array.isArray(entries) ? entries : [])
      .filter(e => String(e?.user_id || '') === uid)
      .slice()
      .sort((a, b) => String(a?.start_date || '').localeCompare(String(b?.start_date || '')));
  }, [entries, selectedUserId]);

  const entriesForAllUsers = useMemo(() => {
    return (Array.isArray(entries) ? entries : [])
      .slice()
      .sort((a, b) => {
        const ua = String(a?.user_id || '');
        const ub = String(b?.user_id || '');
        if (ua !== ub) return ua.localeCompare(ub);
        return String(a?.start_date || '').localeCompare(String(b?.start_date || ''));
      });
  }, [entries]);

  const dayIndex = useMemo(() => buildDayIndex(entries), [entries]);

  const refreshEntries = async (nextRange = range) => {
    setLoadingEntries(true);
    try {
      const res = await fetchVacationEntries({ from: nextRange.from, to: nextRange.to });
      const rows = Array.isArray(res?.entries) ? res.entries : (Array.isArray(res) ? res : []);
      setEntries(rows);
    } catch (err) {
      setEntries([]);
      toast.error(err?.message || 'Failed to load vacations');
    } finally {
      setLoadingEntries(false);
    }
  };

  const onChangePeriod = async (nextKey) => {
    const key = String(nextKey || '').toUpperCase();
    const nextRange = computePeriodRange(key, year);
    setSelectedDate(null);
    setPeriodKey(key);
    setRange(nextRange);
    await refreshEntries(nextRange);
  };

  useEffect(() => {
    if (!selectedDate) return;
    // Smooth scroll to list when a day is selected.
    try {
      entryListRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } catch (_) {
      // ignore
    }
  }, [selectedDate]);

  const loadUsers = async () => {
    setLoadingUsers(true);
    setPeopleNote('');
    try {
      try {
        const res = await fetchAdminUsers({ includeDisabled: false });
        const rows = Array.isArray(res?.users) ? res.users : (Array.isArray(res) ? res : []);

        const viewerIsAdmin = normalizeRole(user?.role) === 'admin' || String(user?.display_name || '').trim().toLowerCase() === 'admin';
        const shouldHideAdminRow = (u) => {
          if (viewerIsAdmin) return false;
          const email = String(u?.email || '').toLowerCase();
          const role = normalizeRole(u?.role);
          return email === 'admin@example.com' || role === 'admin';
        };

        const mapped = rows
          .filter(u => !shouldHideAdminRow(u))
          .map(u => ({
            id: u.id,
            name: u.display_name || u.name || u.username || '—',
            email: u.email || u.username || '—',
            role: u.role || 'employee',
            status: u.status || (u.active === false ? 'disabled' : 'active'),
          }))
          .filter(u => {
            const r = normalizeRole(u.role);
            return r === 'employee' || r === 'junior_manager' || r === 'manager' || r === 'admin';
          })
          .filter(u => !isManagerTestUser(u));

        const sorted = sortUsers(mapped);
        setUsers(sorted);
        return sorted;
      } catch (err) {
        setPeopleNote('User directory endpoint not available yet; showing limited People list.');
      }

      const lookups = await fetchPlanningLookups({ redirectOn401: true });
      const managers = Array.isArray(lookups?.managers) ? lookups.managers : [];
      const employees = Array.isArray(lookups?.employees) ? lookups.employees : [];
      const rows = [
        ...managers.map(m => ({ id: m.id, name: m.display_name || '—', email: '—', role: 'manager', status: 'active' })),
        ...employees.map(e => ({ id: e.id, name: e.display_name || '—', email: '—', role: 'employee', status: 'active' })),
      ].filter(u => !isManagerTestUser(u));
      const sorted = sortUsers(rows);
      setUsers(sorted);
      return sorted;
    } catch (err) {
      setUsers([]);
      toast.error(err?.message || 'Failed to load people');
      return [];
    } finally {
      setLoadingUsers(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const loadedUsers = await loadUsers();
      if (cancelled) return;
      if (!selectedUserId && loadedUsers[0]?.id) setSelectedUserId(loadedUsers[0].id);
      await refreshEntries(range);
    })();
    return () => { cancelled = true; };
  }, []);

  // Add vacation modal
  const [addOpen, setAddOpen] = useState(false);
  const [addSaving, setAddSaving] = useState(false);
  const [addMode, setAddMode] = useState('create'); // create | edit
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    user_id: '',
    type: 'VACATION',
    start_date: '',
    end_date: '',
    note: '',
  });

  const openAdd = () => {
    const defaultUserId = String(selectedUserId || users[0]?.id || '');
    setAddMode('create');
    setEditingId(null);
    setForm({
      user_id: defaultUserId,
      type: 'VACATION',
      start_date: '',
      end_date: '',
      note: '',
    });
    setAddOpen(true);
  };

  const openEdit = (entry) => {
    if (!entry) return;
    setAddMode('edit');
    setEditingId(entry.id);
    setForm({
      user_id: String(entry.user_id || ''),
      type: String(entry.type || 'VACATION').toUpperCase() === 'SICK' ? 'SICK' : 'VACATION',
      start_date: String(entry.start_date || ''),
      end_date: String(entry.end_date || ''),
      note: entry.note == null ? '' : String(entry.note),
    });
    setAddOpen(true);
  };

  const submitAdd = async () => {
    const payload = {
      user_id: String(form.user_id || '').trim(),
      type: String(form.type || '').trim(),
      start_date: String(form.start_date || '').trim(),
      end_date: String(form.end_date || '').trim(),
      note: String(form.note || '').trim(),
    };
    if (!payload.user_id) { toast.error('User is required'); return; }
    if (!['VACATION', 'SICK'].includes(payload.type)) { toast.error('Type is required'); return; }
    if (!payload.start_date) { toast.error('Start date is required'); return; }
    if (!payload.end_date) { toast.error('End date is required'); return; }
    if (payload.end_date < payload.start_date) { toast.error('End date must be on/after start date'); return; }

    setAddSaving(true);
    try {
      if (addMode === 'edit') {
        if (!editingId) {
          toast.error('Missing entry id');
        } else {
          await updateVacationEntry(editingId, {
            user_id: payload.user_id,
            type: payload.type,
            start_date: payload.start_date,
            end_date: payload.end_date,
            note: payload.note || null,
          });
          toast.success('Vacation updated');
        }
      } else {
        await createVacationEntry({
          user_id: payload.user_id,
          type: payload.type,
          start_date: payload.start_date,
          end_date: payload.end_date,
          note: payload.note || null,
        });
        toast.success('Vacation saved');
      }
      setAddOpen(false);
      await refreshEntries();
    } catch (err) {
      if (err?.status === 409) {
        toast.error(err?.message || 'Overlapping entry exists for this user');
      } else {
        toast.error(err?.message || 'Failed to save vacation');
      }
    } finally {
      setAddSaving(false);
    }
  };

  const onDelete = async (entry) => {
    if (!entry?.id) return;
    const ok = window.confirm('Delete this entry?');
    if (!ok) return;
    try {
      await deleteVacationEntry(entry.id);
      toast.success('Entry deleted');
      await refreshEntries();
    } catch (err) {
      toast.error(err?.message || 'Failed to delete entry');
    }
  };

  const userLabelById = useMemo(() => {
    const map = Object.create(null);
    (Array.isArray(users) ? users : []).forEach((u) => {
      map[String(u.id)] = getUserLabel(u);
    });
    return map;
  }, [users]);

  const filteredEntriesForList = useMemo(() => {
    // Default: list ALL entries in selected period.
    // If a date is selected in the calendar, show entries overlapping that day.
    if (!selectedDate) return entriesForAllUsers;

    const matchesDate = (e) => {
      const s = String(e?.start_date || '').slice(0, 10);
      const t = String(e?.end_date || '').slice(0, 10);
      if (!s || !t) return false;
      return s <= selectedDate && t >= selectedDate;
    };

    return entriesForAllUsers.filter(matchesDate);
  }, [entriesForAllUsers, selectedDate]);

  const groupedEntriesForList = useMemo(() => {
    const list = Array.isArray(filteredEntriesForList) ? filteredEntriesForList : [];

    const isActive = (e) => {
      const s = String(e?.start_date || '').slice(0, 10);
      const t = String(e?.end_date || '').slice(0, 10);
      if (!s || !t) return false;
      return s <= todayYmd && todayYmd <= t;
    };

    const byUser = new Map();
    for (const e of list) {
      const uid = String(e?.user_id || '').trim();
      if (!uid) continue;
      const arr = byUser.get(uid) || [];
      arr.push(e);
      byUser.set(uid, arr);
    }

    // Only show users who have at least one entry in the (filtered) selection.
    const order = (Array.isArray(users) ? users : []).map(u => String(u.id));
    const orderedUserIds = [...order.filter(uid => byUser.has(uid)), ...Array.from(byUser.keys()).filter(uid => !order.includes(uid))];

    const groups = orderedUserIds.map((uid) => {
      const rows = (byUser.get(uid) || []).slice().sort((a, b) => {
        const aa = isActive(a);
        const bb = isActive(b);
        if (aa !== bb) return aa ? -1 : 1;
        const as = String(a?.start_date || '');
        const bs = String(b?.start_date || '');
        if (as !== bs) return as.localeCompare(bs);
        const ae = String(a?.end_date || '');
        const be = String(b?.end_date || '');
        if (ae !== be) return ae.localeCompare(be);
        return String(a?.id || '').localeCompare(String(b?.id || ''));
      });
      return {
        user_id: uid,
        user_label: userLabelById[uid] || '—',
        entries: rows,
      };
    });

    return { groups, isActive };
  }, [filteredEntriesForList, todayYmd, userLabelById, users]);

  return (
    <div className="p-6">
      <SectionHeader
        title="Vacations"
        subtitle={`Manage vacation and sick leave • ${periodKey} ${year} • ${range.from} → ${range.to}`}
      />

      <div className="mt-4 grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* Left: users */}
        <div className="lg:col-span-4">
          <Card padded={false} className="p-3">
            <div className="flex items-center justify-between mb-2">
              <div className="font-semibold">People</div>
              {loadingUsers && <div className="text-xs text-gray-600 dark:text-gray-400">Loading…</div>}
            </div>
            {peopleNote ? (
              <div className="text-xs text-gray-600 dark:text-gray-400 mb-2">{peopleNote}</div>
            ) : null}
            <div className="space-y-1">
              {!loadingUsers && users.length === 0 && (
                <div className="text-sm text-gray-600 dark:text-gray-400">No people found.</div>
              )}
              {users.map(u => {
                const isSelected = String(u.id) === String(selectedUserId);
                return (
                  <button
                    key={u.id}
                    type="button"
                    onClick={() => {
                      setSelectedDate(null);
                      setSelectedUserId(u.id);
                    }}
                    className={`w-full text-left px-3 py-2 rounded border ${isSelected ? 'bg-gray-100 dark:bg-gray-800 border-gray-300 dark:border-gray-700' : 'bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/60'}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-medium truncate">{getUserLabel(u)}</div>
                      <div className="text-xs text-gray-600 dark:text-gray-400">{normalizeRole(u.role) === 'junior_manager' ? 'junior_manager' : normalizeRole(u.role)}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </Card>
        </div>

        {/* Right: entries */}
        <div className="lg:col-span-8">
          <Card padded={false} className="p-4 mb-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-center gap-2 flex-wrap">
                <SegmentedControl
                  ariaLabel="Vacation period"
                  ariaRole="tablist"
                  size="sm"
                  options={PERIOD_TABS.map(k => ({ key: k, label: k }))}
                  value={periodKey}
                  onChange={(k) => { if (!loadingEntries) onChangePeriod(k); }}
                />
              </div>

              <div className="flex items-center gap-2">
                <div className="text-xs text-gray-600 dark:text-gray-400">Calendar:</div>
                <SegmentedControl
                  ariaLabel="Vacation calendar mode"
                  ariaRole="tablist"
                  size="sm"
                  options={[
                    { key: 'person', label: 'Person' },
                    { key: 'all', label: 'All' },
                  ]}
                  value={calendarMode}
                  onChange={setCalendarMode}
                />
              </div>
            </div>

            <div className="mt-3">
              {String(periodKey).toUpperCase() === 'YEAR' ? (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-3">
                  {getMonthsForPeriod('YEAR').map((m) => (
                    <MiniMonthGrid
                      key={`m-${m}`}
                      year={year}
                      monthIndex={m}
                      dayIndex={dayIndex}
                      mode={calendarMode}
                      selectedUserId={selectedUserId}
                      selectedDate={selectedDate}
                      onSelectDate={setSelectedDate}
                    />
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {getMonthsForPeriod(periodKey).map((m) => (
                    <MiniMonthGrid
                      key={`m-${m}`}
                      year={year}
                      monthIndex={m}
                      dayIndex={dayIndex}
                      mode={calendarMode}
                      selectedUserId={selectedUserId}
                      selectedDate={selectedDate}
                      onSelectDate={setSelectedDate}
                    />
                  ))}
                </div>
              )}
            </div>
          </Card>

          <Card padded={false} className="p-4">
            <div ref={entryListRef} className="flex items-center justify-between gap-3">
              <div>
                <div className="font-semibold">Vacations</div>
                <div className="text-xs text-gray-600 dark:text-gray-400">
                  {selectedDate ? `Entries overlapping ${selectedDate}` : 'All entries in selected period'}
                </div>
              </div>
              <button
                type="button"
                className="text-sm px-3 py-2 rounded bg-gray-900 text-white disabled:opacity-60"
                onClick={openAdd}
                disabled={!selectedUserId}
              >
                + Add vacation
              </button>
            </div>

            <div className="mt-3">
              {loadingEntries ? (
                <div className="text-sm text-gray-600 dark:text-gray-400">Loading…</div>
              ) : groupedEntriesForList.groups.length === 0 ? (
                <div className="text-sm text-gray-600 dark:text-gray-400">No entries match the current selection.</div>
              ) : (
                <div className="space-y-4">
                  {groupedEntriesForList.groups.map((g) => (
                    <div key={g.user_id} className="rounded border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
                      <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between gap-2">
                        <div className="font-medium truncate">{g.user_label}</div>
                        <div className="text-xs text-gray-600 dark:text-gray-400">{g.entries.length} entries</div>
                      </div>

                      <div className="p-3 space-y-2">
                        {g.entries.map((e) => {
                          const activeNow = groupedEntriesForList.isActive(e);
                          return (
                            <div
                              key={e.id}
                              className={
                                'rounded border p-3 '
                                + (activeNow
                                  ? 'border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60'
                                  : 'border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900')
                              }
                            >
                              <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <Chip label={String(e.type || '').toUpperCase() || '—'} />
                                    {activeNow ? <Chip label="ACTIVE" className="font-semibold" /> : null}
                                    <div className="text-sm font-medium">{e.start_date} → {e.end_date}</div>
                                  </div>
                                  {e.note ? (
                                    <div className="text-sm text-gray-700 dark:text-gray-300 mt-1 break-words">{e.note}</div>
                                  ) : (
                                    <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">No note</div>
                                  )}
                                </div>

                                <div className="flex items-center gap-2">
                                  <button
                                    type="button"
                                    className="text-xs px-2 py-1 rounded border bg-white dark:bg-gray-900"
                                    onClick={() => openEdit(e)}
                                  >
                                    Edit
                                  </button>
                                  <button
                                    type="button"
                                    className="text-xs px-2 py-1 rounded border bg-white dark:bg-gray-900"
                                    onClick={() => onDelete(e)}
                                  >
                                    Delete
                                  </button>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Card>
        </div>
      </div>

      {/* Add vacation modal */}
      {addOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-[6px] p-4">
          <div className="w-full max-w-lg rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="font-semibold">{addMode === 'edit' ? 'Edit vacation' : 'Add vacation'}</div>
              <button type="button" className="text-sm px-2 py-1 rounded border" onClick={() => { if (!addSaving) setAddOpen(false); }}>Close</button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-xs mb-1 text-gray-700 dark:text-gray-300">User</label>
                <select
                  value={form.user_id}
                  onChange={(e) => setForm(f => ({ ...f, user_id: e.target.value }))}
                  className="w-full border rounded px-2 py-2 bg-white dark:bg-gray-900"
                  disabled={addSaving}
                >
                  <option value="">Select user…</option>
                  {users.map(u => (
                    <option key={u.id} value={u.id}>{getUserLabel(u)}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs mb-1 text-gray-700 dark:text-gray-300">Type</label>
                <select
                  value={form.type}
                  onChange={(e) => setForm(f => ({ ...f, type: e.target.value }))}
                  className="w-full border rounded px-2 py-2 bg-white dark:bg-gray-900"
                  disabled={addSaving}
                >
                  <option value="VACATION">VACATION</option>
                  <option value="SICK">SICK</option>
                </select>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs mb-1 text-gray-700 dark:text-gray-300">Start date</label>
                  <input
                    type="date"
                    value={form.start_date}
                    onChange={(e) => setForm(f => ({ ...f, start_date: e.target.value }))}
                    className="w-full border rounded px-2 py-2 bg-white dark:bg-gray-900"
                    disabled={addSaving}
                  />
                </div>
                <div>
                  <label className="block text-xs mb-1 text-gray-700 dark:text-gray-300">End date</label>
                  <input
                    type="date"
                    value={form.end_date}
                    onChange={(e) => setForm(f => ({ ...f, end_date: e.target.value }))}
                    className="w-full border rounded px-2 py-2 bg-white dark:bg-gray-900"
                    disabled={addSaving}
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs mb-1 text-gray-700 dark:text-gray-300">Note (optional)</label>
                <textarea
                  value={form.note}
                  onChange={(e) => setForm(f => ({ ...f, note: e.target.value }))}
                  className="w-full border rounded px-2 py-2 bg-white dark:bg-gray-900"
                  rows={3}
                  placeholder="Optional note…"
                  disabled={addSaving}
                />
              </div>

              <div className="flex items-center justify-end gap-2 pt-2">
                <button
                  type="button"
                  className="text-sm px-3 py-2 rounded border"
                  onClick={() => setAddOpen(false)}
                  disabled={addSaving}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="text-sm px-3 py-2 rounded bg-gray-900 text-white disabled:opacity-60"
                  onClick={submitAdd}
                  disabled={addSaving}
                >
                  {addSaving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
